/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2022 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Component from 'flarum/common/Component';
import type { ComponentAttrs } from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import type Mithril from 'mithril';
import loadDynLib from '../../common/helpers/loadDynLib';

export interface SwiperAutoplayOptions {
	delay: number;
	pauseOnMouseEnter: boolean;
	disableOnInteraction: boolean;
}

export interface SwiperNavigationOptions {
	nextEl: string;
	prevEl: string;
}

export interface SwiperPaginationOptions {
	el: string;
	clickable: boolean;
	dynamicBullets: boolean;
}

export interface SwiperOptions {
	grabCursor?: boolean;
	centeredSlides?: boolean;
	slidesPerView?: number | 'auto';
	loop?: boolean;
	speed?: number;
	/** `false` disables autoplay, which is what a single-slide slider wants. */
	autoplay?: SwiperAutoplayOptions | false;
	/** `false` hides the previous/next buttons. */
	navigation?: SwiperNavigationOptions | false;
	/** `false` hides the pagination bullets. */
	pagination?: SwiperPaginationOptions | false;
	/** Effect-specific keys (`coverflowEffect`, `fadeEffect`, …) that individual sliders add. */
	[key: string]: unknown;
}

export interface SwiperAutoplayControl {
	start(): void;
	stop(): void;
}

export interface SwiperInstance {
	/** Pick up slides that were added or removed after the slider was created. */
	update(): void;

	/** Scroll to the slide at `index`, taking `speed` milliseconds. */
	slideTo(index: number, speed?: number): void;

	autoplay: SwiperAutoplayControl;
}

// Swiper is fetched on demand by `loadDynLib(['swiper'])` and installs itself as a browser global,
// so it has to be read off `window` once the library has loaded rather than imported.
type SwiperConstructor = new (container: string, options?: SwiperOptions) => SwiperInstance;

export interface SliderItemAttrs<TItem> extends ComponentAttrs {
	item: TItem;
	className: string;
	index: number;
}

/**
 * A component rendered for one slide.
 */
export type SliderItemComponent<TItem> = new (attrs: SliderItemAttrs<TItem>) => Mithril.ClassComponent<SliderItemAttrs<TItem>>;

/**
 * Not used within this extension itself; ported from GB Core for dependent extensions.
 *
 * Used by:
 * - flarum-ext-orell-facade: HomeSlider
 * - flarum-ext-orell-rezensionen: ReviewsCoverflow
 *
 * Styles for the rendered .Slider/.SliderItem markup live in resources/less/Slider.less.
 *
 * Subclasses derive their slides by overriding `getItems()` and name the component each slide
 * renders through `sliderItemComponent`. Sliders that load their items asynchronously can keep
 * assigning `items` directly instead — the default `getItems()` leaves whatever is already there
 * untouched.
 */
export default abstract class AbstractSlider<TItem = unknown, TAttrs extends ComponentAttrs = ComponentAttrs> extends Component<TAttrs> {
	items: TItem[] = [];
	id!: string;
	swiper?: SwiperInstance;

	/** Subclasses may flip this while they fetch `items`. */
	loading: boolean = false;

	/** The component rendered for each slide. */
	abstract get sliderItemComponent(): SliderItemComponent<TItem>;

	/**
	 * Derives the slides from the current attrs. Override this rather than assigning `items` from
	 * a lifecycle hook: the base re-runs it on every update, before `view()`, so a slider stays in
	 * step with its attrs without each subclass having to rediscover that `onbeforeupdate` — not
	 * `onupdate` — is the hook that gets there in time.
	 *
	 * The default returns `items` unchanged, so sliders that fill the field themselves (after an
	 * async load, say) keep working untouched.
	 */
	protected getItems(_attrs: TAttrs): TItem[] {
		return this.items;
	}

	oninit(vnode: Mithril.Vnode<TAttrs, this>) {
		super.oninit(vnode);

		this.items = this.getItems(vnode.attrs);

		// Generate a unique id for the slider (only letters and numbers)
		this.id = `slider_${Math.random().toString(36).substring(2, 9)}`;
	}

	onbeforeupdate(vnode: Mithril.VnodeDOM<TAttrs, this>) {
		// `super` is what refreshes `this.attrs`, so it has to run first — and has to run at all.
		super.onbeforeupdate(vnode);

		this.items = this.getItems(vnode.attrs);
	}

	oncreate(vnode: Mithril.VnodeDOM<TAttrs, this>) {
		super.oncreate(vnode);

		this.loadSwiper();
	}

	onupdate(vnode: Mithril.VnodeDOM<TAttrs, this>) {
		super.onupdate(vnode);

		this.loadSwiper();
	}

	/**
	 * Swiper ships a global stylesheet alongside its script, so an empty slider that loaded it
	 * anyway would restyle every page it renders on. `initOrUpdateSwiper()` already gives up
	 * without items; this keeps the fetch from happening in the first place.
	 */
	protected loadSwiper(): void {
		if (!this.items.length) {
			return;
		}

		loadDynLib(['swiper']).then(() => {
			this.initOrUpdateSwiper();
		});
	}

	/**
	 * The options every slider starts from. Override {@link swiperOptionOverrides} to change
	 * individual keys; override this only to replace the baseline wholesale.
	 */
	protected defaultSwiperOptions(): SwiperOptions {
		let autoplay: SwiperAutoplayOptions | false = {
			delay: 5000,
			pauseOnMouseEnter: true,
			disableOnInteraction: false,
		};

		let navigation: SwiperNavigationOptions | false = {
			nextEl: `.${this.id}.swiper-button-next`,
			prevEl: `.${this.id}.swiper-button-prev`,
		};

		let pagination: SwiperPaginationOptions | false = {
			el: `.${this.id}.swiper-pagination`,
			clickable: true,
			dynamicBullets: true,
		};

		if (this.items.length === 1) {
			autoplay = false;
			navigation = false;
			pagination = false;
		}

		return {
			grabCursor: true,
			centeredSlides: true,
			slidesPerView: 'auto',
			loop: true,
			autoplay: autoplay,
			speed: 600,
			navigation: navigation,
			pagination: pagination,
		};
	}

	/**
	 * The keys this slider wants to change, merged over {@link defaultSwiperOptions}. Returning
	 * only what differs keeps a subclass on the baseline for everything else, so later
	 * improvements to the defaults reach it instead of being shadowed by a restated copy.
	 */
	protected swiperOptionOverrides(): Partial<SwiperOptions> {
		return {};
	}

	getSwiperOptions(): SwiperOptions {
		return { ...this.defaultSwiperOptions(), ...this.swiperOptionOverrides() };
	}

	/**
	 * @deprecated Read or override {@link getSwiperOptions} instead — having both spellings means
	 * overriding one while something else reads the other silently does nothing.
	 */
	get swiperOptions(): SwiperOptions {
		return this.getSwiperOptions();
	}

	initOrUpdateSwiper(): void {
		const sliderClass = `.${this.id}.Slider-body`;

		// If Slider-body is present in the DOM
		if (!document.querySelector(sliderClass)) {
			return;
		}

		if (!this.items.length) {
			return;
		}

		if (this.swiper) {
			this.swiper.update();
			if (this.getSwiperOptions().autoplay) {
				this.swiper.autoplay.start();
			}
			return;
		}

		const Swiper = (window as { Swiper?: SwiperConstructor }).Swiper;
		if (!Swiper) {
			return;
		}

		this.swiper = new Swiper(sliderClass, this.getSwiperOptions());
		this.onSwiperCreated(this.swiper);
	}

	/**
	 * Hook for sliders that need to drive the instance once it exists — jumping to a
	 * preselected slide, for instance.
	 */
	onSwiperCreated(_swiper: SwiperInstance): void {}

	getClassName(): string {
		return 'Slider';
	}

	/**
	 * @deprecated Read or override {@link getClassName} instead — having both spellings means
	 * overriding one while something else reads the other silently does nothing.
	 */
	get className(): string {
		return this.getClassName();
	}

	view(): Mithril.Children {
		const { items } = this;

		if (!items.length) {
			return <div className={this.getClassName()}>{this.loading && <LoadingIndicator />}</div>;
		}

		const onlyOne = items.length === 1;

		const SliderItem = this.sliderItemComponent;

		return (
			<div className={this.getClassName()}>
				<div className={`Slider-body ${this.id} swiper ${onlyOne ? 'single-item' : ''}`}>
					<div className={`${this.id} swiper-wrapper`}>
						{items.map((item, i) => (
							<SliderItem item={item} className={`${this.id} swiper-slide`} index={i} />
						))}
					</div>
					{!onlyOne && (
						<div className={`Slider-body--navigation`}>
							<div className={`${this.id} swiper-button-prev`}></div>
							<div className={`${this.id} swiper-pagination`}></div>
							<div className={`${this.id} swiper-button-next`}></div>
						</div>
					)}
				</div>
			</div>
		);
	}
}
